import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  X, ShieldCheck, Inbox, Bot, GanttChartSquare, Bell, CheckCheck, AlertTriangle, Info, Settings2,
} from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { relativeTime } from "@/lib/format";

const CAT_ICON = {
  approval: ShieldCheck,
  directive: Inbox,
  agent: Bot,
  schedule: GanttChartSquare,
  task: CheckCheck,
  system: Settings2,
};

const PRIO_STYLE = {
  critical: { border: "border-st-critical", bg: "bg-st-critical/5", icon: "text-st-critical", Icon: AlertTriangle },
  warning: { border: "border-st-warning", bg: "bg-st-warning/5", icon: "text-st-warning", Icon: AlertTriangle },
  success: { border: "border-st-success", bg: "bg-white", icon: "text-st-success", Icon: CheckCheck },
  info: { border: "border-border", bg: "bg-white", icon: "text-st-info", Icon: Info },
};

export default function NotificationCenter({ onClose }) {
  const { companyParam, companyId } = useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: items = [] } = useQuery({
    queryKey: ["notifications", companyId],
    queryFn: async () => (await api.get("/notifications", { params: companyParam })).data,
    refetchInterval: 15000,
  });

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["notif-summary"] });
  };
  const markAll = async () => {
    await api.post("/notifications/read-all", null, { params: companyParam });
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["notif-summary"] });
  };

  const go = (n) => {
    markRead(n.id);
    if (n.entity_type === "approval") navigate(`/approvals/${n.entity_id}`);
    else if (n.entity_type === "job") navigate(`/jobs/${n.entity_id}`);
    onClose();
  };

  const pinned = items.filter((n) => n.pinned && !n.read);
  const rest = items.filter((n) => !(n.pinned && !n.read));

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        data-testid="notification-center"
        className="absolute right-0 top-12 w-[400px] max-h-[78vh] bg-white border border-ink shadow-2xl z-50 flex flex-col animate-in-up"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-ink text-white">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4" strokeWidth={2} />
            <span className="font-heading text-sm font-semibold uppercase tracking-wide">Notifications</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={markAll} data-testid="mark-all-read-btn" className="text-[11px] uppercase tracking-wider text-white/70 hover:text-white px-2 py-1">
              Mark all read
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center hover:bg-white/10">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto">
          {items.length === 0 && (
            <div className="py-12 text-center text-sm text-ink-muted">You're all caught up.</div>
          )}
          {pinned.length > 0 && (
            <div className="px-3 pt-3 pb-1 text-[10px] font-semibold tracking-[0.16em] uppercase text-st-critical">
              Pinned · Critical
            </div>
          )}
          {pinned.map((n) => <NotifRow key={n.id} n={n} onClick={() => go(n)} onRead={() => markRead(n.id)} />)}
          {pinned.length > 0 && rest.length > 0 && (
            <div className="px-3 pt-3 pb-1 text-[10px] font-semibold tracking-[0.16em] uppercase text-ink-faint">Earlier</div>
          )}
          {rest.map((n) => <NotifRow key={n.id} n={n} onClick={() => go(n)} onRead={() => markRead(n.id)} />)}
        </div>
      </div>
    </>
  );
}

function NotifRow({ n, onClick, onRead }) {
  const cat = CAT_ICON[n.category] || Info;
  const ps = PRIO_STYLE[n.priority] || PRIO_STYLE.info;
  const CatIcon = cat;
  return (
    <div
      data-testid="notification-item"
      onClick={onClick}
      className={`relative border-l-4 ${ps.border} ${n.read ? "bg-white opacity-70" : ps.bg} px-3 py-3 border-b border-border cursor-pointer hover:bg-surface-muted transition-colors`}
    >
      <div className="flex items-start gap-2.5">
        <CatIcon className={`w-4 h-4 mt-0.5 shrink-0 ${ps.icon}`} strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-ink leading-tight flex-1">{n.title}</p>
            {!n.read && <span className="w-2 h-2 bg-brand shrink-0" />}
          </div>
          <p className="text-xs text-ink-muted mt-0.5 leading-snug">{n.message}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="font-mono text-[10px] text-ink-faint">{relativeTime(n.created_at)}</span>
            {n.needs_action && n.action_label && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-brand">{n.action_label} →</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
